import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { ESTRELLAS_INFO, numEstrellas } from "../../utils/estrellas";

const todayISO = () => new Date().toISOString().slice(0, 10);

// ─────────────────────────────────────────────
// Botones en columna (semáforo y variantes)
// ─────────────────────────────────────────────
const BotonesOpciones = ({ opciones, value, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
    {opciones.map((op) => (
      <button
        key={op.val}
        onClick={() => onChange(op.val)}
        style={{
          padding: "22px 0",
          borderRadius: 16,
          border: value === op.val ? "4px solid #1e3a5f" : "4px solid transparent",
          background: op.bg,
          color: "#fff",
          fontSize: 22,
          fontWeight: 800,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          boxShadow: value === op.val ? "0 0 0 3px rgba(30,58,95,0.35)" : "0 3px 8px rgba(0,0,0,0.18)",
          transform: value === op.val ? "scale(1.03)" : "scale(1)",
          transition: "all 0.15s",
        }}
      >
        <span style={{ fontSize: 28 }}>{op.emoji}</span>
        {op.label}
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────
// Semáforo
// ─────────────────────────────────────────────
const RespuestaSemaforo = ({ value, onChange }) => (
  <BotonesOpciones value={value} onChange={onChange} opciones={[
    { val: "verde",    label: "Bien",    bg: "#16a34a", emoji: "🟢" },
    { val: "amarillo", label: "Regular", bg: "#d97706", emoji: "🟡" },
    { val: "rojo",     label: "Mal",     bg: "#dc2626", emoji: "🔴" },
  ]} />
);

// ─────────────────────────────────────────────
// Semáforo + Desconozco
// ─────────────────────────────────────────────
const RespuestaSemaforoDesconozco = ({ value, onChange }) => (
  <BotonesOpciones value={value} onChange={onChange} opciones={[
    { val: "verde",      label: "Bien",       bg: "#16a34a", emoji: "🟢" },
    { val: "amarillo",   label: "Regular",    bg: "#d97706", emoji: "🟡" },
    { val: "rojo",       label: "Mal",        bg: "#dc2626", emoji: "🔴" },
    { val: "desconozco", label: "Desconozco", bg: "#94a3b8", emoji: "❓" },
  ]} />
);

// ─────────────────────────────────────────────
// Sí / No
// ─────────────────────────────────────────────
const RespuestaSiNo = ({ value, onChange }) => (
  <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
    {[
      { val: "si", label: "Sí", bg: "#16a34a", emoji: "✅" },
      { val: "no", label: "No", bg: "#dc2626", emoji: "❌" },
    ].map((op) => (
      <button
        key={op.val}
        onClick={() => onChange(op.val)}
        style={{
          flex: 1,
          padding: "24px 0",
          borderRadius: 16,
          border: value === op.val ? "4px solid #1e3a5f" : "4px solid transparent",
          background: op.bg,
          color: "#fff",
          fontSize: 22,
          fontWeight: 800,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          boxShadow: value === op.val ? "0 0 0 3px rgba(30,58,95,0.35)" : "0 3px 8px rgba(0,0,0,0.18)",
          transform: value === op.val ? "scale(1.03)" : "scale(1)",
          transition: "all 0.15s",
        }}
      >
        <span style={{ fontSize: 28 }}>{op.emoji}</span>
        {op.label}
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────
// Estrellas (1-5): el valor es un número entero
// ─────────────────────────────────────────────
const RespuestaEstrellas = ({ value, onChange }) => {
  const sel = numEstrellas(value);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const on = sel !== null && n <= sel;
          return (
            <button
              key={n}
              onClick={() => onChange(n)}
              aria-label={`${n} estrella${n !== 1 ? "s" : ""}`}
              style={{
                flex: 1, maxWidth: 76, padding: "14px 0", borderRadius: 14, cursor: "pointer", fontSize: 38, lineHeight: 1,
                border: on ? `3px solid ${ESTRELLAS_INFO[sel].bg}` : "3px solid #e2e8f0",
                background: on ? ESTRELLAS_INFO[sel].bg : "#fff",
                color: on ? "#fff" : "#cbd5e1",
                boxShadow: on ? "0 3px 8px rgba(0,0,0,0.18)" : "0 2px 6px rgba(0,0,0,0.06)",
                transition: "all 0.15s",
              }}
            >★</button>
          );
        })}
      </div>
      <div style={{ textAlign: "center", marginTop: 12, fontSize: 18, fontWeight: 800, color: sel ? ESTRELLAS_INFO[sel].bg : "#94a3b8", minHeight: 26 }}>
        {sel ? `${sel} / 5 · ${ESTRELLAS_INFO[sel].label}` : "Toca una estrella (1 = muy mal, 5 = muy bien)"}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Opciones personalizadas de color (tipo 'opciones_color')
// ─────────────────────────────────────────────
// multiple: value es un array de ids (en texto); si no, un único id.
const RespuestaOpcionesColor = ({ valores, value, onChange, multiple = false }) => {
  const sel = multiple ? (Array.isArray(value) ? value : []) : null;
  const activo = (id) => (multiple ? sel.includes(id) : value === id);
  const pulsar = (id) => {
    if (!multiple) return onChange(id);
    const nuevo = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    onChange(nuevo.length ? nuevo : null);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
      {(valores || []).map((op) => {
        const id = String(op.id), on = activo(id);
        return (
          <button
            key={op.id}
            onClick={() => pulsar(id)}
            style={{
              padding: "22px 0",
              borderRadius: 16,
              border: on ? "4px solid #1e3a5f" : "4px solid transparent",
              background: op.color || "#3b82f6",
              color: "#fff",
              fontSize: 20,
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: on ? "0 0 0 3px rgba(30,58,95,0.35)" : "0 3px 8px rgba(0,0,0,0.18)",
              transform: on ? "scale(1.03)" : "scale(1)",
              opacity: multiple && sel.length > 0 && !on ? 0.75 : 1,
              transition: "all 0.15s",
            }}
          >
            {multiple && <span style={{ marginRight: 10 }}>{on ? "☑" : "☐"}</span>}
            {op.etiqueta}
          </button>
        );
      })}
    </div>
  );
};

// Tipo "seleccion_residentes": un botón por residente del piso + "Ninguno"
// (excluyente). value = array de ids en texto ("123") o ["ninguno"].
const NINGUNO = "ninguno";
const RespuestaResidentes = ({ residentes, multiple, value, onChange }) => {
  const sel = Array.isArray(value) ? value : [];
  const pulsar = (clave) => {
    if (clave === NINGUNO) return onChange(sel.includes(NINGUNO) ? null : [NINGUNO]);
    if (!multiple) return onChange(sel.includes(clave) ? null : [clave]);
    const sinNinguno = sel.filter((x) => x !== NINGUNO);
    const nuevo = sinNinguno.includes(clave) ? sinNinguno.filter((x) => x !== clave) : [...sinNinguno, clave];
    onChange(nuevo.length ? nuevo : null);
  };
  const boton = (clave, contenido, activo, colorActivo) => (
    <button key={clave} onClick={() => pulsar(clave)} style={{
      display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
      padding: "16px 18px", borderRadius: 16, cursor: "pointer", fontSize: 18, fontWeight: 800,
      border: activo ? `3px solid ${colorActivo}` : "3px solid #e2e8f0",
      background: activo ? (colorActivo === "#16a34a" ? "#dcfce7" : "#ede9fe") : "#fff",
      color: "#1e293b", boxShadow: activo ? "0 0 0 3px rgba(30,58,95,0.12)" : "0 2px 6px rgba(0,0,0,0.08)",
      transition: "all 0.15s",
    }}>
      <span style={{
        width: 30, height: 30, flexShrink: 0, borderRadius: multiple ? 8 : "50%", display: "flex", alignItems: "center", justifyContent: "center",
        border: `2.5px solid ${activo ? colorActivo : "#94a3b8"}`, background: activo ? colorActivo : "#fff", color: "#fff", fontSize: 18,
      }}>{activo ? "✓" : ""}</span>
      {contenido}
    </button>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
      {residentes.length === 0 && (
        <div style={{ fontSize: 15, color: "#64748b", background: "#f8fafc", borderRadius: 12, padding: 14 }}>No hay residentes dados de alta en este piso.</div>
      )}
      {residentes.map((a) => boton(String(a.id_alumno), (
        <span style={{ display: "flex", flexDirection: "column" }}>
          <span>{a.nombre}</span>
          {a.habitacion && <span style={{ fontSize: 13, fontWeight: 600, color: "#7c3aed" }}>Hab. {a.habitacion}</span>}
        </span>
      ), sel.includes(String(a.id_alumno)), "#7c3aed"))}
      {boton(NINGUNO, <span>Ninguno</span>, sel.includes(NINGUNO), "#16a34a")}
    </div>
  );
};

// ─────────────────────────────────────────────
// Panel principal
// ─────────────────────────────────────────────
const ReporteDiario = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const usuario = user?.user || user || "";
  // Reutilizable por supervisora y asistenta: cada rol responde sus propias preguntas (a_qn)
  const rol = user?.tipo === "asistenta" ? "asistenta" : "supervisora";

  const piso = location.state?.piso;
  // Desde Reportes (supervisora): modo "residente" (un reporte por alumno), "sustitucion", o diario de un día retrasado (fecha_dia)
  const modo = location.state?.modo || null;
  const alumno = location.state?.alumno || null;
  const idSustitucion = location.state?.id_sustitucion || null;
  const fechaDia = location.state?.fecha_dia || null;
  const subtitulo = modo === "residente" ? "Reporte de residente" : modo === "sustitucion" ? "Reporte de sustitución" : "Reporte diario";
  const fechaBonita = fechaDia ? new Date(fechaDia + "T12:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" }) : null;

  const [preguntas, setPreguntas] = useState([]);
  const [residentes, setResidentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [idx, setIdx] = useState(0);
  // respuestas: { [clave]: valor }  — clave = "id" o "id_idAlumno" para residentes
  const [respuestas, setRespuestas] = useState({});

  // Borrador en localStorage: una clave por usuario+piso (no por día, para no ir
  // acumulando una entrada nueva cada día) con la fecha guardada DENTRO del propio
  // borrador. Al cargar se compara esa fecha con la de hoy: si no coincide (borrador
  // de un día anterior que se quedó sin enviar) se descarta en vez de restaurarlo,
  // para que nunca aparezcan preguntas ya contestadas de otro día.
  const STORAGE_KEY = piso ? `reporteDiario_borrador_${usuario}_${piso.id}${modo ? `_${modo}_${alumno?.id_alumno || idSustitucion || ""}` : ""}${fechaDia ? `_${fechaDia}` : ""}` : null;
  const borradorRestauradoRef = useRef(false);

  const [tooltipAbierto, setTooltipAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState("");

  // Cronometraje: cuánto tarda en responder cada pregunta y el total del reporte.
  const pasoInicioRef = useRef(Date.now());
  const inicioTotalRef = useRef(Date.now());
  const tiemposRef = useRef({}); // clave -> segundos
  const cronometroIniciadoRef = useRef(false);

  useEffect(() => {
    if (!piso) return;
    const pedirPreguntas = axios.get(`/api/servicio/reporte_opciones?a_qn=${modo || rol}&solo_activas=1`);
    // Los residentes se usan en las preguntas de categoría "Residentes" (solo
    // supervisora) y en las de tipo "seleccion_residentes" (cualquier rol).
    const pedirAlumnos = axios.get(`/api/servicio/alumnos_piso/${piso.id}`);
    Promise.all([pedirPreguntas, pedirAlumnos])
      .then(([resPreg, resAlum]) => {
        setPreguntas(resPreg.data || []);
        setResidentes(resAlum.data || []);
        setError("");
      })
      .catch(() => setError("❌ Error al cargar preguntas."))
      .finally(() => setLoading(false));
  }, [piso, rol, modo]);

  // Preguntas normales en orden de BBDD, luego por cada alumno todas sus preguntas de Residentes
  const pasos = useMemo(() => {
    const sortedPreguntas = [...preguntas].sort((a, b) => (a.orden || 0) - (b.orden || 0));
    // en modo residente / sustitución todas las preguntas son del propio reporte (sin el bucle por alumno del reporte diario)
    const normales = sortedPreguntas.filter((p) => modo || p.categoria !== "Residentes");
    const deResidentes = sortedPreguntas.filter((p) => p.categoria === "Residentes");
    const result = normales.map((p) => ({ pregunta: p, residente: null }));
    if (!modo && rol === "supervisora" && deResidentes.length > 0 && residentes.length > 0) {
      for (const r of residentes) {
        for (const p of deResidentes) {
          result.push({ pregunta: p, residente: r });
        }
      }
    }
    return result;
  }, [preguntas, residentes, rol, modo]);

  // Restaurar borrador (una sola vez, en cuanto ya sabemos qué pasos hay que
  // responder): valida la fecha guardada y descarta cualquier respuesta cuya
  // clave ya no corresponda a un paso actual (p.ej. una pregunta desactivada
  // entre medias, o un residente que ya no está en el piso).
  useEffect(() => {
    if (borradorRestauradoRef.current) return;
    if (!STORAGE_KEY || pasos.length === 0) return;
    borradorRestauradoRef.current = true;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const guardado = JSON.parse(raw);
      if (!guardado || guardado.fecha !== todayISO()) {
        // Borrador de otro día (o corrupto): se descarta, nunca se restaura.
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const clavesValidas = new Set(
        pasos.map((ps) => (ps.residente ? `${ps.pregunta.id}_${ps.residente.id_alumno}` : `${ps.pregunta.id}`))
      );
      const respuestasFiltradas = {};
      Object.entries(guardado.respuestas || {}).forEach(([clave, valor]) => {
        if (clavesValidas.has(clave)) respuestasFiltradas[clave] = valor;
      });
      if (Object.keys(respuestasFiltradas).length > 0) {
        setRespuestas(respuestasFiltradas);
      }
      if (typeof guardado.idx === "number" && guardado.idx >= 0 && guardado.idx < pasos.length) {
        setIdx(guardado.idx);
      }
    } catch (e) {
      console.warn("No se pudo restaurar el borrador del reporte:", e);
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    }
  }, [STORAGE_KEY, pasos]);

  // Autoguardado: cada vez que cambian las respuestas o el paso actual, se
  // vuelca todo a localStorage (mientras no se haya enviado ya el reporte).
  useEffect(() => {
    if (!STORAGE_KEY) return;
    if (!borradorRestauradoRef.current) return; // evita sobreescribir antes de restaurar
    if (enviado) return;
    if (Object.keys(respuestas).length === 0) return; // nada que guardar todavía
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ fecha: todayISO(), respuestas, idx }));
    } catch (e) {
      console.warn("No se pudo guardar el borrador del reporte:", e);
    }
  }, [STORAGE_KEY, respuestas, idx, enviado]);

  // Cerrar tooltip al pulsar fuera
  useEffect(() => {
    if (!tooltipAbierto) return;
    const handler = () => setTooltipAbierto(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [tooltipAbierto]);

  // Arranca el cronómetro (total + primera pregunta) en cuanto hay pasos que responder,
  // para no contar el tiempo de carga de preguntas como tiempo de respuesta.
  useEffect(() => {
    if (cronometroIniciadoRef.current || pasos.length === 0) return;
    cronometroIniciadoRef.current = true;
    pasoInicioRef.current = Date.now();
    inicioTotalRef.current = Date.now();
  }, [pasos]);

  if (!piso) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p style={{ color: "#dc2626" }}>No se ha indicado el piso. Vuelve atrás.</p>
        <button onClick={() => navigate(-1)} style={s.btnVolver}>← Volver</button>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>Cargando preguntas…</div>;
  if (error)   return <div style={{ padding: 40, textAlign: "center", color: "#dc2626" }}>{error}</div>;
  if (pasos.length === 0) return (
    <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
      <p>No hay preguntas configuradas en el reporte.</p>
      <button onClick={() => navigate(-1)} style={s.btnVolver}>← Volver</button>
    </div>
  );

  const paso = pasos[idx];
  const { pregunta, residente } = paso;
  const total = pasos.length;
  const claveActual = residente ? `${pregunta.id}_${residente.id_alumno}` : `${pregunta.id}`;
  const valorActual = respuestas[claveActual] ?? null;
  const esUltima = idx === total - 1;
  const vacia = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
  const todasRespondidas = pasos.every((ps) => {
    if (ps.pregunta.opcional) return true;
    const k = ps.residente ? `${ps.pregunta.id}_${ps.residente.id_alumno}` : `${ps.pregunta.id}`;
    return !vacia(respuestas[k]);
  });

  // Auto-avance al pulsar: todos los tipos de botón salvo texto y la selección múltiple de residentes.
  const esSeleccionMultiple = (pregunta.tipo === "seleccion_residentes" || pregunta.tipo === "opciones_color") && !!pregunta.respuesta_multiple;
  const esBoton = pregunta.tipo !== "texto" && !esSeleccionMultiple;

  // Cierra el cronómetro de la pregunta que se está abandonando y arranca el de la nueva.
  const irAPaso = (nuevoIdx) => {
    const segundos = (Date.now() - pasoInicioRef.current) / 1000;
    tiemposRef.current[claveActual] = Math.round(segundos * 10) / 10;
    pasoInicioRef.current = Date.now();
    setIdx(nuevoIdx);
  };

  const responder = (val) => {
    setRespuestas((prev) => {
      if (val === null) { const { [claveActual]: _quitada, ...resto } = prev; return resto; }
      return { ...prev, [claveActual]: val };
    });
    setTooltipAbierto(false);
    // auto-avanzar al siguiente en tipos de botón
    if (esBoton && val !== null && idx < total - 1) {
      setTimeout(() => irAPaso(idx + 1), 300);
    }
  };

  const irSiguiente = () => {
    if (idx < total - 1) { irAPaso(idx + 1); setTooltipAbierto(false); }
  };

  const irAnterior = () => {
    if (idx > 0) { irAPaso(idx - 1); setTooltipAbierto(false); }
  };

  const enviar = async () => {
    if (!todasRespondidas) return;
    setEnviando(true);
    setErrorEnvio("");
    try {
      // Cerrar el cronómetro de la última pregunta visible antes de construir el payload.
      const segundosUltimoPaso = (Date.now() - pasoInicioRef.current) / 1000;
      tiemposRef.current[claveActual] = Math.round(segundosUltimoPaso * 10) / 10;

      const respuestasPayload = Object.entries(respuestas).map(([clave, valor]) => {
        const partes = clave.split("_");
        const tiempo_segundos = tiemposRef.current[clave] ?? null;
        if (partes.length === 2) {
          return { id_pregunta: parseInt(partes[0]), id_alumno: parseInt(partes[1]), valor, tiempo_segundos };
        }
        const preg = preguntas.find((p) => String(p.id) === clave);
        if (preg?.tipo === "seleccion_residentes" && Array.isArray(valor)) {
          // Se guardan los nombres de ese momento (el reporte se sigue leyendo
          // igual aunque luego el alumno cambie de piso o se dé de baja).
          if (valor.includes(NINGUNO)) return { id_pregunta: parseInt(clave), valor: NINGUNO, alumnos: [], tiempo_segundos };
          const alumnos = valor.map((id) => {
            const a = residentes.find((x) => String(x.id_alumno) === id);
            return { id_alumno: parseInt(id), nombre: a?.nombre || `Alumno ${id}`, habitacion: a?.habitacion || null };
          });
          return { id_pregunta: parseInt(clave), valor: alumnos.map((a) => a.nombre).join(", "), alumnos, tiempo_segundos };
        }
        return { id_pregunta: parseInt(clave), valor, tiempo_segundos };
      });
      const tiempo_total_segundos = Math.round((Date.now() - inicioTotalRef.current) / 100) / 10;
      if (modo === "residente") await axios.post("/api/tareas/residentes", { id_piso: piso.id, id_alumno: alumno.id_alumno, fecha_dia: fechaDia, respuestas: respuestasPayload, tiempo_total_segundos });
      else if (modo === "sustitucion") await axios.post("/api/tareas/sustituciones", { id_sustitucion: idSustitucion, fecha_dia: fechaDia, respuestas: respuestasPayload, tiempo_total_segundos });
      else await axios.post("/api/servicio/reportes", { id_piso: piso.id, respuestas: respuestasPayload, tiempo_total_segundos, ...(fechaDia ? { fecha_dia: fechaDia } : {}) });
      // Reporte ya guardado en el servidor: el borrador local ya no hace falta.
      if (STORAGE_KEY) {
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      }
      setEnviado(true);
    } catch (e) {
      setErrorEnvio(e.response?.data?.error || "❌ Error al enviar el reporte.");
    } finally {
      setEnviando(false);
    }
  };

  // ── Pantalla de confirmación ──
  if (enviado) {
    return (
      <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div style={{ fontSize: 72, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: "#15803d", marginBottom: 8 }}>Reporte enviado</h2>
        <p style={{ color: "#166534", fontSize: 16, marginBottom: 32 }}>
          El reporte de <strong>{piso.abreviatura}</strong>{alumno ? <> · <strong>{alumno.nombre}</strong></> : null} se ha guardado correctamente.
        </p>
        <button onClick={() => navigate(-1)} style={{ ...s.btnVolver, padding: "14px 32px", fontSize: 16 }}>
          ← Volver al listado
        </button>
      </div>
    );
  }

  return (
    <div style={{ height: "100dvh", background: "#f0f4f8", display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Cabecera fija */}
      <div style={{ background: "#1e3a5f", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", padding: "8px 14px" }}>
          ← Volver
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, opacity: 0.75 }}>{subtitulo}{fechaBonita ? ` · ${fechaBonita}` : ""}</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>{piso.abreviatura}{alumno ? ` · ${alumno.nombre}` : ""}{modo === "sustitucion" && location.state?.sustituta ? ` · ${location.state.sustituta}` : ""}</div>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, opacity: 0.85 }}>
          {idx + 1} / {total}
        </div>
      </div>

      {/* Barra de progreso */}
      <div style={{ height: 5, background: "#dbeafe" }}>
        <div style={{ height: "100%", background: "#3b82f6", width: `${((idx + 1) / total) * 100}%`, transition: "width 0.3s" }} />
      </div>

      {/* Contenido */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", padding: "24px 20px 20px", maxWidth: 540, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>

        {/* Categoría */}
        <div style={{ fontSize: 26, fontWeight: 900, color: "#1e3a5f", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>
          {pregunta.categoria}
        </div>

        {/* Residente (solo para preguntas de categoría Residentes) */}
        {residente && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, background: "#ede9fe", borderRadius: 10, padding: "8px 14px", border: "1px solid #c4b5fd" }}>
            <span style={{ fontSize: 22 }}>🧑</span>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#5b21b6" }}>{residente.nombre}</div>
              {residente.habitacion && (
                <div style={{ fontSize: 13, color: "#7c3aed" }}>Hab. {residente.habitacion}</div>
              )}
            </div>
          </div>
        )}

        {/* Subcategoría + emoji + botón info */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          {pregunta.emoji && <span style={{ fontSize: 34 }}>{pregunta.emoji}</span>}
          <h2 style={{ fontSize: 27, fontWeight: 800, color: "#1e3a5f", margin: 0, flex: 1, lineHeight: 1.25 }}>
            {pregunta.subcategoria}
            {!!pregunta.opcional && (
              <span style={{ fontSize: 14, fontWeight: 700, color: "#0369a1", background: "#e0f2fe", borderRadius: 8, padding: "2px 8px", marginLeft: 10, verticalAlign: "middle" }}>
                Opcional
              </span>
            )}
          </h2>
          {pregunta.descripcion && (
            <div style={{ position: "relative" }}>
              <button
                onClick={(e) => { e.stopPropagation(); setTooltipAbierto((v) => !v); }}
                style={{ background: "#e0f2fe", border: "none", borderRadius: "50%", width: 34, height: 34, fontSize: 17, cursor: "pointer", color: "#0369a1", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                ⓘ
              </button>
              {tooltipAbierto && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ position: "absolute", right: 0, top: 42, background: "#fff", border: "1.5px solid #bae6fd", borderRadius: 12, padding: "12px 16px", width: 240, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 99, fontSize: 14, color: "#334155", lineHeight: 1.5 }}
                >
                  {pregunta.descripcion}
                </div>
              )}
            </div>
          )}
        </div>

        {esSeleccionMultiple && (
          <div style={s.avisoMultiple}>☑️ Puedes marcar varias opciones.</div>
        )}

        {/* Respuesta según tipo */}
        {pregunta.tipo === "semaforo" && (
          <RespuestaSemaforo value={valorActual} onChange={responder} />
        )}
        {pregunta.tipo === "semaforo+desconozco" && (
          <RespuestaSemaforoDesconozco value={valorActual} onChange={responder} />
        )}
        {pregunta.tipo === "si_no" && (
          <RespuestaSiNo value={valorActual} onChange={responder} />
        )}
        {pregunta.tipo === "estrellas" && (
          <RespuestaEstrellas value={valorActual} onChange={responder} />
        )}
        {pregunta.tipo === "opciones_color" && (
          <RespuestaOpcionesColor valores={pregunta.valores} value={valorActual} onChange={responder} multiple={!!pregunta.respuesta_multiple} />
        )}
        {pregunta.tipo === "seleccion_residentes" && (
          <RespuestaResidentes residentes={residentes} multiple={!!pregunta.respuesta_multiple} value={valorActual} onChange={responder} />
        )}
        {pregunta.tipo === "texto" && (
          <textarea
            value={valorActual || ""}
            onChange={(e) => responder(e.target.value)}
            placeholder="Escribe tu respuesta…"
            rows={5}
            style={{ width: "100%", padding: "14px", fontSize: 16, borderRadius: 14, border: "1.5px solid #cbd5e1", resize: "vertical", outline: "none", marginTop: 8, boxSizing: "border-box", lineHeight: 1.6 }}
          />
        )}

        {esSeleccionMultiple && (
          <div style={{ marginTop: 22, fontSize: 14.5, color: "#475569", fontWeight: 600, textAlign: "center" }}>
            Cuando termines de marcar, pulsa «{esUltima ? "Enviar reporte" : "Siguiente"}» 👇
          </div>
        )}

        {/* Navegación */}
        <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
          {idx > 0 && (
            <button onClick={irAnterior} style={s.btnSecundario}>
              ← Anterior
            </button>
          )}
          {!esUltima ? (
            <button
              onClick={irSiguiente}
              disabled={!pregunta.opcional && vacia(valorActual)}
              style={{ ...s.btnPrimario, flex: 1, opacity: (!pregunta.opcional && vacia(valorActual)) ? 0.4 : 1 }}
            >
              Siguiente →
            </button>
          ) : (
            <button
              onClick={enviar}
              disabled={!todasRespondidas || enviando}
              style={{ ...s.btnEnviar, flex: 1, opacity: (!todasRespondidas || enviando) ? 0.4 : 1 }}
            >
              {enviando ? "Enviando…" : "✅ Enviar reporte"}
            </button>
          )}
        </div>

        {/* Puntos indicadores */}
        <div style={{ display: "flex", justifyContent: "center", gap: 7, marginTop: 28, flexWrap: "wrap" }}>
          {pasos.map((ps, i) => {
            const k = ps.residente ? `${ps.pregunta.id}_${ps.residente.id_alumno}` : `${ps.pregunta.id}`;
            const respondida = respuestas[k] !== undefined && respuestas[k] !== "";
            return (
              <button
                key={i}
                onClick={() => { irAPaso(i); setTooltipAbierto(false); }}
                title={ps.residente ? `${ps.pregunta.subcategoria} – ${ps.residente.nombre}` : ps.pregunta.subcategoria}
                style={{
                  width: 12, height: 12, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0,
                  background: i === idx ? "#1e3a5f" : respondida ? "#3b82f6" : "#cbd5e1",
                  transform: i === idx ? "scale(1.4)" : "scale(1)",
                  transition: "all 0.15s",
                }}
              />
            );
          })}
        </div>

        {/* Resumen preguntas pendientes */}
        {esUltima && !todasRespondidas && (
          <div style={{ marginTop: 20, background: "#fff7ed", borderRadius: 12, padding: "12px 16px", border: "1px solid #fed7aa" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#c2410c", marginBottom: 6 }}>
              ⚠️ Preguntas sin responder:
            </div>
            {pasos
              .filter((ps) => {
                if (ps.pregunta.opcional) return false;
                const k = ps.residente ? `${ps.pregunta.id}_${ps.residente.id_alumno}` : `${ps.pregunta.id}`;
                return respuestas[k] === undefined || respuestas[k] === "";
              })
              .map((ps, i) => (
                <button
                  key={i}
                  onClick={() => irAPaso(pasos.indexOf(ps))}
                  style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", padding: "4px 0", fontSize: 14, color: "#9a3412", textDecoration: "underline" }}
                >
                  {ps.pregunta.emoji} {ps.pregunta.subcategoria}{ps.residente ? ` – ${ps.residente.nombre}` : ""}
                </button>
              ))}
          </div>
        )}

        {errorEnvio && (
          <div style={{ marginTop: 14, background: "#fee2e2", borderRadius: 10, padding: "10px 14px", color: "#b91c1c", fontSize: 14 }}>
            {errorEnvio}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  avisoMultiple: {
    display: "inline-block", alignSelf: "flex-start", marginBottom: 12, background: "#eef2ff", color: "#3730a3",
    border: "1.5px solid #c7d2fe", borderRadius: 999, padding: "6px 14px", fontSize: 14.5, fontWeight: 700,
  },
  btnVolver: {
    padding: "10px 22px", borderRadius: 20,
    background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
    color: "#fff", border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: 15,
    boxShadow: "0 2px 8px rgba(29,78,216,0.35)",
  },
  btnPrimario: {
    padding: "16px 0", borderRadius: 14,
    background: "#1e3a5f", color: "#fff",
    border: "none", cursor: "pointer",
    fontWeight: 800, fontSize: 17,
    boxShadow: "0 3px 10px rgba(30,58,95,0.25)",
  },
  btnSecundario: {
    padding: "16px 20px", borderRadius: 14,
    background: "#e2e8f0", color: "#475569",
    border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: 15,
  },
  btnEnviar: {
    padding: "18px 0", borderRadius: 14,
    background: "linear-gradient(135deg, #16a34a, #15803d)",
    color: "#fff", border: "none", cursor: "pointer",
    fontWeight: 800, fontSize: 18,
    boxShadow: "0 3px 12px rgba(22,163,74,0.35)",
  },
};

export default ReporteDiario;
